import { useMutation } from '@northladder/services';
import {
  ErrorEventsEnum,
  errorLogToRemoteUtil,
  IApiError,
} from '@northladder/utilities';

import { ILoginTypeData } from '../types';
import { USER_ACCOUNTS_API } from '../userAccountsAPIClient';

/**
 * This is to track the login type mutation keys in react query cache.
 */
const MUT_LOGIN_TYPE_USER = 'login-type-mutation-key';

interface IUseLoginOptions {
  onErrorCallback?: (err: IApiError) => void;
  /**
   * Callback to be by the calling app if the login is successful.
   * Note: Usually this would be best handled within the onSuccess of the hook
   * itself but since the login would require additional platform specific
   * features like `next/router` & `localStorage` on the web and
   * `react-navigation` & `asyncStorage` on mobile, this will be done in the
   * callback of the respective platform without having to embed them in the
   * util.
   */
  onSuccessCallback: (loginData: ILoginTypeData) => void;
}

/**
 * This performs a login request and retrieves the authed user info and token.
 * Provides data fetching states and a callback to be used in the redirection
 * later.
 */
export const useLoginTypeMutation = ({
  onErrorCallback,
  onSuccessCallback,
}: IUseLoginOptions) => {
  const { mutate, isPending, error, data } = useMutation({
    mutationKey: [MUT_LOGIN_TYPE_USER],
    networkMode: 'always', // Even make calls when offline
    retry: false, // For login Request, do not retry failed requests.
    mutationFn: USER_ACCOUNTS_API.checkLoginTypeUser,
    onSuccess: onSuccessCallback,
    onError: (err: IApiError) => {
      errorLogToRemoteUtil({
        error,
        errorCode: ErrorEventsEnum.ERROR_IN_API_CALL,
        errorTitle: 'Error in Session renewal Attempt',
        message: error?.message,
      });

      onErrorCallback?.(err);

      return err;
    },
  });

  return {
    data,
    error,
    isPending,
    submitLoginType: mutate,
  };
};
